/**
 * Origem de mídia da aplicação.
 *
 * Uma decisão importante mora aqui: a existência da origem **não é assumida pela
 * configuração**, é descoberta em tempo de execução. O app tenta buscar o
 * `manifest.json` da base de mídia; se ele responder, os downloads ficam disponíveis
 * e restritos ao que o manifest lista. Se não responder, não há origem — e nenhum
 * botão de download aparece.
 *
 * Isso evita o pior dos dois mundos: um botão que sempre falha numa instalação sem
 * vídeos, ou a ausência de download numa instalação que os tem.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { logger } from '@/lib/logger';
import { MediaResolver } from '@/services/media/MediaResolver';
import { fetchMediaManifest, type MediaManifest } from '@/services/media/mediaManifest';
import { AuthorizedDirectMediaSource, MEDIA_BASE_URL } from '@/services/media/sources';
import { mediaLookup } from '@/storage/index';

interface MediaContextValue {
  resolver: MediaResolver;
  /** `null` enquanto a origem ainda está sendo verificada. */
  manifest: MediaManifest | null;
  /** Quantos sinais a origem publica. Zero quando não há origem. */
  publishedCount: number;
  ready: boolean;
}

const MediaContext = createContext<MediaContextValue | null>(null);

export function MediaProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [manifest, setManifest] = useState<MediaManifest | null>(null);
  const [ready, setReady] = useState(false);

  // A origem é criada uma vez; o manifest é injetado quando chega. Recriar o
  // resolvedor faria o player remontar e perder a reprodução em andamento.
  const downloadSource = useMemo(() => new AuthorizedDirectMediaSource(MEDIA_BASE_URL), []);

  const resolver = useMemo(
    () => new MediaResolver({ localMedia: mediaLookup(), downloadSource }),
    [downloadSource],
  );

  useEffect(() => {
    let cancelled = false;
    let carregado = false;

    const carregar = async (): Promise<void> => {
      if (cancelled || carregado) return;

      try {
        const loaded = await fetchMediaManifest(MEDIA_BASE_URL);
        if (cancelled) return;

        // `strict` implícito: com manifest carregado, só o que ele lista é baixável.
        downloadSource.setManifest(loaded);
        setManifest(loaded);
        carregado = loaded !== null;

        logger.info(
          'mídia',
          loaded
            ? `origem disponível com ${Object.keys(loaded.items).length} vídeos`
            : 'origem de mídia indisponível; reprodução apenas online',
        );
      } catch (error) {
        if (cancelled) return;
        // Manifest inválido é problema de configuração da origem, não do app.
        // Seguir sem origem é melhor que oferecer downloads que vão falhar.
        downloadSource.setManifest(null, true);
        carregado = true;
        logger.warn('mídia', 'manifest da origem é inválido; downloads desativados', error);
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    void carregar();

    /**
     * Abrir o app sem rede deixaria a origem indisponível pela sessão inteira, mesmo
     * depois de a conexão voltar. Uma nova tentativa quando a aba volta ao primeiro
     * plano cobre exatamente o caso de sair do modo avião e continuar usando.
     */
    const tentarDeNovo = (): void => {
      if (document.visibilityState === 'visible') void carregar();
    };

    document.addEventListener('visibilitychange', tentarDeNovo);
    window.addEventListener('online', tentarDeNovo);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', tentarDeNovo);
      window.removeEventListener('online', tentarDeNovo);
    };
  }, [downloadSource]);

  const value = useMemo<MediaContextValue>(
    () => ({
      resolver,
      manifest,
      publishedCount: manifest ? Object.keys(manifest.items).length : 0,
      ready,
    }),
    [resolver, manifest, ready],
  );

  return <MediaContext.Provider value={value}>{children}</MediaContext.Provider>;
}

export function useMedia(): MediaContextValue {
  const context = useContext(MediaContext);
  if (!context) throw new Error('useMedia precisa estar dentro de <MediaProvider>');
  return context;
}
